import React, { useState, useEffect, useRef } from 'react';
import {
    AppBar, Toolbar, Typography, Button, List, ListItem, ListItemIcon,
    ListItemText, Paper, Box, IconButton, Card, CardContent, Avatar,
    LinearProgress, Chip, Radio, Checkbox, FormControlLabel,
    FormGroup, Alert
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import Replay10 from '@mui/icons-material/Replay10';
import {
    ArrowBack, PlayArrow, Quiz, CheckCircle,
    RadioButtonUnchecked, ExpandMore, ExpandLess, Timer,
    BookmarkBorder, Bookmark, OndemandVideo, Article, Fullscreen,
    Refresh, Speed,
    FullscreenExit
} from '@mui/icons-material';
import ReactPlayer from 'react-player';
import screenfull from 'screenfull';

const UserCourseScreen = ({ course, onBack }) => {
    const videoRef = useRef(null);
    const [currentModule, setCurrentModule] = useState(null);
    const [currentContent, setCurrentContent] = useState(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [completedItems, setCompletedItems] = useState(new Set(course.currentProgress || []));
    const [expandedModule, setExpandedModule] = useState(null);
    const [bookmarked, setBookmarked] = useState([]);
    const [progress, setProgress] = useState(0);
    const [currentProgress, setCurrentProgress] = useState(course.currentProgress);
    const [progressID, setProgressID] = useState(course.progressID);
    const [showQuiz, setShowQuiz] = useState(false);
    const [quizAnswers, setQuizAnswers] = useState({});
    const [quizSubmitted, setQuizSubmitted] = useState(false);
    const [quizResult, setQuizResult] = useState(null);
    const [fullscreen, setFullscreen] = useState(false);
    const [isFullscreen, setIsFullScreen] = useState(false);

    const [quizAttempts, setQuizAttempts] = useState({}); // Track attempts for each quiz
    const [duration, setDuration] = useState(0);
    const [currentTime, setCurrentTime] = useState(0);
    const [playbackRate, setPlaybackRate] = useState(1.0);
    const [resumeTime, setResumeTime] = useState(0);

    useEffect(() => {
        if (currentContent) {
            if (currentContent.video.isVideo) {
                const savedTime = localStorage.getItem(`video-time-${currentContent._id}`);
                if (savedTime) {
                    setResumeTime(parseFloat(savedTime));
                }
            }
        }

    }, [currentContent]);

    const handleProgress = ({ playedSeconds }) => {
        setCurrentTime(playedSeconds);
        localStorage.setItem(`video-time-${currentContent._id}`, playedSeconds);
    };

    const handleEnded = () => {
        sendUpdate(currentContent._id);
        localStorage.removeItem(`video-time-${currentContent._id}`);
    };

    const handleSpeedChange = () => {
        const speeds = [0.5, 1.0, 1.5, 2.0];
        const nextSpeed = speeds[(speeds.indexOf(playbackRate) + 1) % speeds.length];
        setPlaybackRate(nextSpeed);
    };



    useEffect(() => {
        if (screenfull.isEnabled) {
            screenfull.on('change', () => {
                if (!screenfull.isFullscreen) {
                    setIsFullScreen(false);
                    setIsPlaying(false); // Pause the video when exiting fullscreen
                }
            });
        }

        return () => {
            if (screenfull.isEnabled) {
                screenfull.off('change');
            }
        };
    }, []);

    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.hidden) {
                setIsPlaying(false); // Pause the video when tab is hidden
            }
        };

        document.addEventListener("visibilitychange", handleVisibilityChange);

        return () => {
            document.removeEventListener("visibilitychange", handleVisibilityChange);
        };
    }, []);



    useEffect(() => {
        console.log(course);
        if (course.modules?.length > 0) {
            if (course.initialContent) {
                // If we have an initial content selected, use it
                setCurrentModule(course.modules[course.initialModuleIndex]);
                setExpandedModule(course.modules[course.initialModuleIndex]._id);
                setCurrentContent(course.initialContent);
            } else if (!currentModule) {
                // Otherwise use default first module/content
                setCurrentModule(course.modules[0]);
                setExpandedModule(course.modules[0]._id);
                if (course.modules[0].videosWithQuizzes.length > 0) {
                    setCurrentContent(course.modules[0].videosWithQuizzes[0]);
                }
            }
        }
        calculateProgress();
    }, [course, completedItems]);

    useEffect(() => {
        // Reset quiz state when content changes
        setShowQuiz(false);
        setQuizAnswers({});
        setQuizSubmitted(false);
        setQuizResult(null);
    }, [currentContent]);

    useEffect(() => {
        getCourseData();
    }, []);

    const formatTime = (seconds) => {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = Math.floor(seconds % 60);
        return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
    };

    const handleRewind = () => {
        const player = document.querySelector('video');
        if (player) {
            player.currentTime = Math.max(0, player.currentTime - 10);
        }
    };

    const getCourseData = async () => {
        try {
            const token = localStorage.getItem('token');
            const userId = localStorage.getItem('userId');

            const response = await fetch('https://beiguru.co.in/employee/courses', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': token || ''
                },
                body: JSON.stringify({
                    "employeeID": userId || '',
                    "courseID": course.id
                })
            });

            if (!response.ok) {
                if (response.status == 401) {
                    localStorage.clear();
                    window.location.reload();
                    return [];
                }
            }

            const data = await response.json();
            console.log(data);
            setCurrentProgress(data.existingProgress.currentProgress);
            setProgressID(data.existingProgress._id);
        } catch (error) {
            console.error(error);
        }
    };

    async function sendUpdate(id) {
        const token = localStorage.getItem('token');
        const headers = {
            'Authorization': token || '',
            'Content-Type': 'application/json'
        };

        const requestBody = JSON.stringify({
            currentProgress: id,
            isCompleted: true
        });

        try {
            const response = await fetch(`https://beiguru.co.in/api/progress/${progressID}`, {
                method: 'PUT',
                headers: headers,
                body: requestBody
            });

            if (response.ok) {
                const jsonResponse = await response.json();
                console.log(jsonResponse);
            } else {
                console.error('Failed to update progress:', response.statusText);
            }
        } catch (error) {
            console.error('Error while updating progress:', error.message);
        }
    }

    const calculateProgress = () => {
        const totalItems = course.modules.reduce((acc, module) =>
            acc + module.videosWithQuizzes.length, 0);
        const completedCount = completedItems.size;
        setProgress((completedCount / totalItems) * 100);
    };

    const handleModuleSelect = (module, content) => {
        setCurrentModule(module);
        setCurrentContent(content);

        if (!content.video.isVideo) {
            setIsPlaying(false);
            sendUpdate(content._id);
        } else {
            setResumeTime(0);
            setIsPlaying(true);
        }
    };

    const toggleBookmark = (contentId) => {
        setBookmarked(prev =>
            prev.includes(contentId)
                ? prev.filter(id => id !== contentId)
                : [...prev, contentId]
        );
    };

    const handleQuizAnswer = (questionId, answer, isMCQ) => {
        if (isMCQ) {
            setQuizAnswers(prev => ({
                ...prev,
                [questionId]: answer
            }));
        } else {
            const currentAnswers = quizAnswers[questionId] || [];
            const updatedAnswers = currentAnswers.includes(answer)
                ? currentAnswers.filter(a => a !== answer)
                : [...currentAnswers, answer];
            setQuizAnswers(prev => ({
                ...prev,
                [questionId]: updatedAnswers
            }));
        }
    };

    const handleQuizSubmit = () => {
        let correctCount = 0;
        const quiz = currentContent.quiz;

        quiz.forEach(question => {
            if (question.isMCQ) {
                if (quizAnswers[question._id] === question.correctAnswer) {
                    correctCount++;
                }
            } else {
                const userAnswers = quizAnswers[question._id] || [];
                const correctAnswers = question.correctAnswer.split(',');
                if (userAnswers.length === correctAnswers.length &&
                    userAnswers.every(answer => correctAnswers.includes(answer))) {
                    correctCount++;
                }
            }
        });

        const score = (correctCount / quiz.length) * 100;
        setQuizResult({
            score,
            total: quiz.length,
            correct: correctCount
        });
        setQuizSubmitted(true);

        // Update attempts count
        const currentAttempts = (quizAttempts[currentContent._id] || 0) + 1;
        setQuizAttempts(prev => ({
            ...prev,
            [currentContent._id]: currentAttempts
        }));

        if (score >= 60) {
            setCompletedItems(prev => new Set([...prev, currentContent._id]));
            sendUpdate(currentContent._id + '_quiz');
            courseCompleted(course.id, currentContent._id, score);
        }
    };

    const handleQuizRetake = () => {
        setQuizAnswers({});
        setQuizSubmitted(false);
        setQuizResult(null);
    };

    const getRemainingAttempts = () => {
        const usedAttempts = quizAttempts[currentContent._id] || 0;
        return Math.max(3 - usedAttempts, 0);
    };

    const toggleFullscreen = () => {
        if (screenfull.isEnabled && videoRef.current) {
            screenfull.toggle(videoRef.current);
            setFullscreen(!fullscreen);
        }
    };


    const courseCompleted = async (courseID, materialID, marks) => {
        const token = localStorage.getItem('token') || '';
        const userId = localStorage.getItem('userId') || '';

        const requestOptions = {
            method: 'POST',
            headers: {
                'Authorization': token,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                employeeID: userId,
                courseID: courseID,
                materialID: materialID,
                marks: marks,
            })
        };

        try {
            const response = await fetch('https://beiguru.co.in/quiz-marks-completed', requestOptions);
            if (response.ok) {
                // Handle success
            } else {
                // Handle failure
            }
        } catch (error) {
            // Handle error
        }
    };

    const renderQuizContent = () => (
        <Card
            elevation={0}
            sx={{
                bgcolor: 'white',
                border: '1px solid',
                borderColor: 'grey.200',
                borderRadius: 4,
                height: '100%',
                overflow: 'auto'
            }}
        >
            <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                    <Typography variant="h6" sx={{ color: 'grey.900', fontWeight: 600 }}>
                        Knowledge Check: {currentContent?.video.title}
                    </Typography>
                    <Button
                        onClick={() => setShowQuiz(false)}
                        startIcon={<ArrowBack />}
                        variant="outlined"
                    >
                        Back to Content
                    </Button>
                </Box>

                {quizSubmitted && quizResult && (
                    <Box sx={{ mb: 4 }}>
                        <Alert
                            severity={quizResult.score >= 70 ? "success" : "warning"}
                            sx={{ mb: 2 }}
                        >
                            You scored {quizResult.score}% ({quizResult.correct} out of {quizResult.total} correct)
                        </Alert>

                        {quizResult.score < 70 && getRemainingAttempts() > 0 && (
                            <Button
                                variant="contained"
                                startIcon={<Refresh />}
                                onClick={handleQuizRetake}
                                sx={{ mr: 2 }}
                            >
                                Retake Quiz ({getRemainingAttempts()} attempts remaining)
                            </Button>
                        )}

                        {getRemainingAttempts() === 0 && quizResult.score < 70 && (
                            <Alert severity="error" sx={{ mt: 2 }}>
                                You have used all your attempts for this quiz. Please contact your instructor for assistance.
                            </Alert>
                        )}
                    </Box>
                )}

                {currentContent?.quiz.map((question, index) => (
                    <Box key={question._id} sx={{ mb: 4 }}>
                        <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
                            {index + 1}. {question.question}
                        </Typography>

                        {question.isMCQ ? (
                            <FormGroup>
                                {question.options
                                    .filter(Boolean)
                                    .map((option, optIndex) => (
                                        <FormControlLabel
                                            key={optIndex}
                                            control={
                                                <Radio
                                                    checked={quizAnswers[question._id] === option}
                                                    onChange={() => handleQuizAnswer(question._id, option, true)}
                                                    disabled={quizSubmitted}
                                                />
                                            }
                                            label={option}
                                        />
                                    ))}
                            </FormGroup>
                        ) : (
                            <FormGroup>
                                {question.options
                                    .filter(Boolean)
                                    .map((option, optIndex) => (
                                        <FormControlLabel
                                            key={optIndex}
                                            control={
                                                <Checkbox
                                                    checked={(quizAnswers[question._id] || []).includes(optIndex.toString())}
                                                    onChange={() => handleQuizAnswer(question._id, optIndex.toString(), false)}
                                                    disabled={quizSubmitted}
                                                />
                                            }
                                            label={option}
                                        />
                                    ))}
                            </FormGroup>
                        )}

                        {quizSubmitted && question.notes && (
                            <Alert severity="info" sx={{ mt: 2 }}>
                                {question.notes}
                            </Alert>
                        )}
                    </Box>
                ))}

                {!quizSubmitted && getRemainingAttempts() > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Button
                            variant="contained"
                            onClick={handleQuizSubmit}
                        >
                            Submit Answers
                        </Button>
                        <Typography variant="body2" color="text.secondary">
                            Attempts remaining: {getRemainingAttempts()}
                        </Typography>
                    </Box>
                )}
            </CardContent>
        </Card>
    );

    // Rest of the component remains the same...
    const renderContent = () => {

        if (!currentContent) {
            return (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '70vh',
                        gap: 3,
                        bgcolor: 'grey.50',
                        borderRadius: 4
                    }}
                >
                    <OndemandVideo sx={{ fontSize: 64, color: 'grey.300' }} />
                    <Typography variant="h5" color="textSecondary">
                        Select a module to begin learning
                    </Typography>
                    <Button
                        variant="outlined"
                        onClick={() => handleModuleSelect(course.modules[0], course.modules[0].videosWithQuizzes[0])}
                        startIcon={<PlayArrow />}
                    >
                        Start First Module
                    </Button>
                </Box>
            );
        }

        return (
            <>
                <Card
                    elevation={0}
                    sx={{
                        height: '70vh',
                        backgroundColor: currentContent.video.isVideo ? '#000' : '#fff',
                        position: 'relative',
                        overflow: 'hidden',
                        borderRadius: 4,
                    }}>
                    {currentContent.video.isVideo ? (
                        <Box sx={{ height: '100%', position: 'relative' }}>
                            <ReactPlayer
                                url={`https://beiguru.co.in/${currentContent.video.video}`}
                                width="100%"
                                ref={videoRef}
                                height="100%"
                                playing={isPlaying}
                                playbackRate={playbackRate}
                                onEnded={handleEnded}
                                onProgress={handleProgress}
                                onDuration={(duration) => setDuration(duration)}
                                controls={false}
                                autoPlay={true}
                                loop={false}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setIsPlaying(prev => !prev);
                                }}
                                progressInterval={1000}
                                onStart={() => {
                                    console.log(videoRef);

                                    videoRef.current.seekTo(resumeTime, 'seconds');
                                }}
                            />
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: 16,
                                    right: 16,
                                    display: 'flex',
                                    gap: 1
                                }}
                            >
                                <IconButton
                                    onClick={() => toggleBookmark(currentContent._id)}
                                    sx={{
                                        bgcolor: 'rgba(255, 255, 255, 0.1)',
                                        '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.2)' }
                                    }}
                                >
                                    {bookmarked.includes(currentContent._id) ?
                                        <Bookmark sx={{ color: 'white' }} /> :
                                        <BookmarkBorder sx={{ color: 'white' }} />
                                    }
                                </IconButton>
                                <IconButton
                                    onClick={toggleFullscreen}
                                    sx={{
                                        bgcolor: 'rgba(255, 255, 255, 0.1)',
                                        '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.2)' }
                                    }}
                                >
                                    {isFullscreen ? <FullscreenExit sx={{ color: 'white' }} /> : <Fullscreen sx={{ color: 'white' }} />}
                                </IconButton>
                            </Box>
                            <Box
                                sx={{
                                    position: 'absolute',
                                    bottom: 16,
                                    left: 16,
                                    right: 16,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 2,
                                    color: 'white',
                                    bgcolor: 'rgba(0, 0, 0, 0.5)',
                                    padding: '8px 16px',
                                    borderRadius: 2
                                }}
                            >
                                <IconButton
                                    onClick={() => {
                                        if (videoRef.current) {
                                            videoRef.current.seekTo(Math.max(currentTime - 10, 0), 'seconds');
                                        }
                                    }}
                                    sx={{ color: 'white' }}
                                >
                                    <Replay10 />
                                </IconButton>
                                <Typography variant="body2">
                                    {formatTime(currentTime)} / {formatTime(duration)}
                                </Typography>
                                <IconButton onClick={handleSpeedChange} sx={{ color: 'white' }}>
                                    <Speed />
                                </IconButton>
                                <Typography variant="body2">{playbackRate}x</Typography>
                            </Box>
                        </Box>
                    ) : (
                        <iframe
                            src={`https://beiguru.co.in/${currentContent.video.video}`}
                            width="100%"
                            height="100%"
                            style={{ border: 'none' }}
                        />
                    )}
                    {isPlaying ? null : (
                        <div
                            onClick={(e) => {
                                e.stopPropagation();
                                setIsPlaying(prev => !prev);
                            }}
                            style={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                zIndex: 2,
                                boxShadow: '0px 5px 5px rgba(0, 0, 0, 0.2)',
                                padding: '5px',
                                borderRadius: '50%',
                                backgroundColor: '#2076D2'
                            }}>
                            <IconButton>
                                {isPlaying ? <PauseIcon fontSize="large" /> : <PlayArrowIcon style={{ color: 'white' }} fontSize="large" />}
                            </IconButton>
                        </div>
                    )}
                </Card>
                <Card
                    elevation={0}
                    sx={{
                        bgcolor: 'white',
                        border: '1px solid',
                        borderColor: 'grey.200',
                        borderRadius: 4
                    }}
                >
                    <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                            <Typography variant="h6" sx={{ color: 'grey.900', fontWeight: 600 }}>
                                {currentContent.video.title}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <Chip
                                    icon={currentContent.video.isVideo ? <OndemandVideo /> : <Article />}
                                    label={currentContent.video.isVideo ? "Video" : "Document"}
                                    size="small"
                                    variant="outlined"
                                />
                                {completedItems.has(currentContent._id) && (
                                    <Chip
                                        icon={<CheckCircle />}
                                        label="Completed"
                                        color="success"
                                        size="small"
                                        variant="outlined"
                                    />
                                )}
                            </Box>
                        </Box>
                        <Typography color="grey.600">
                            {currentContent.video.description}
                        </Typography>

                        {currentContent.quiz?.length > 0 && (
                            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center', gap: 2, alignItems: 'center' }}>
                                <Button
                                    variant="contained"
                                    startIcon={<Quiz />}
                                    onClick={() => setShowQuiz(true)}
                                    size="large"
                                    disabled={getRemainingAttempts() === 0 && !completedItems.has(currentContent._id)}
                                >
                                    Start Knowledge Check
                                </Button>
                                {getRemainingAttempts() < 3 && (
                                    <Typography variant="body2" color="text.secondary">
                                        {getRemainingAttempts()} attempts remaining
                                    </Typography>
                                )}
                            </Box>
                        )}
                    </CardContent>
                </Card>
            </>
        );
    };

    const percent = (currentProgress.length / course.total) * 100;
    const percentInt = isNaN(percent) || !isFinite(percent) ? 0 : Math.min(percent, 100);

    return (
        <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'scroll' }}>
            <AppBar position="static" elevation={0} sx={{ bgcolor: 'white', flexShrink: 0 }}>
                <Toolbar sx={{ borderBottom: 1, borderColor: 'grey.200' }}>
                    <IconButton edge="start" onClick={onBack} sx={{ mr: 2, color: 'grey.700' }}>
                        <ArrowBack />
                    </IconButton>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" sx={{ color: 'grey.900', fontWeight: 600 }}>
                            {course.title}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 0.5 }}>
                            <LinearProgress
                                variant="determinate"
                                value={percentInt}
                                sx={{
                                    width: 200,
                                    height: 6,
                                    borderRadius: 3,
                                    bgcolor: 'grey.100',
                                    '& .MuiLinearProgress-bar': {
                                        borderRadius: 3,
                                        bgcolor: 'primary.main'
                                    }
                                }}
                            />
                            <Typography variant="body2" color="primary">
                                {Number(percentInt).toFixed(0)}% Complete
                            </Typography>
                        </Box>
                    </Box>
                </Toolbar>
            </AppBar>

            <Box sx={{
                flex: 1,
                display: 'flex',
                gap: 3,
                p: 3,
                bgcolor: 'grey.50',
            }}>
                {!showQuiz && (
                    <Paper
                        elevation={0}
                        sx={{
                            width: 360,
                            flexShrink: 0,
                            borderRadius: 2,
                            border: 1,
                            borderColor: 'grey.200',
                            bgcolor: 'white',
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: 'column'
                        }}
                    >
                        <List disablePadding>
                            {course.modules.map((module, moduleIndex) => (
                                <Box key={module._id}>
                                    <ListItem
                                        button
                                        onClick={() => setExpandedModule(
                                            expandedModule === module._id ? null : module._id
                                        )}
                                        sx={{
                                            borderBottom: 1,
                                            borderColor: 'grey.200',
                                            py: 2
                                        }}
                                    >
                                        <Avatar
                                            sx={{
                                                mr: 2,
                                                bgcolor: 'primary.50',
                                                color: 'primary.main',
                                                width: 32,
                                                height: 32
                                            }}
                                        >
                                            {moduleIndex + 1}
                                        </Avatar>
                                        <ListItemText
                                            primary={module.title}
                                            secondary={
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                                    <Timer sx={{ fontSize: 16, color: 'grey.500' }} />
                                                    <Typography variant="body2" color="grey.500">
                                                        {module.videosWithQuizzes.length} items
                                                    </Typography>
                                                </Box>
                                            }
                                        />
                                        {expandedModule === module._id ? <ExpandLess /> : <ExpandMore />}
                                    </ListItem>

                                    {expandedModule === module._id && (
                                        <List disablePadding>
                                            {module.videosWithQuizzes.map(content => (
                                                <ListItem
                                                    key={content._id}
                                                    button
                                                    selected={currentContent?._id === content._id}
                                                    onClick={() => handleModuleSelect(module, content)}
                                                    sx={{
                                                        pl: 4,
                                                        borderLeft: currentContent?._id === content._id ? 4 : 0,
                                                        borderLeftColor: 'primary.main',
                                                        bgcolor: currentContent?._id === content._id ?
                                                            'primary.50' : 'transparent'
                                                    }}
                                                >
                                                    <ListItemIcon>
                                                        {content.video.isVideo ?
                                                            <PlayArrow color="inherit" /> :
                                                            <Article color="inherit" />
                                                        }
                                                    </ListItemIcon>
                                                    <ListItemText
                                                        primary={content.video.title}
                                                        secondary={
                                                            <Typography
                                                                variant="body2"
                                                                color="grey.600"
                                                                noWrap
                                                                sx={{ maxWidth: 200 }}
                                                            >
                                                                {content.video.description}
                                                            </Typography>
                                                        }
                                                    />
                                                    {completedItems.has(content._id) && (content.quiz.length > 0 ? completedItems.has(content._id + '_quiz') : true) ?
                                                        <CheckCircle color="success" /> :
                                                        <RadioButtonUnchecked color="disabled" />
                                                    }
                                                </ListItem>
                                            ))}
                                        </List>
                                    )}
                                </Box>
                            ))}
                        </List>
                    </Paper>
                )}

                <Box sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    overflow: 'scroll'
                }}>
                    {showQuiz ? renderQuizContent() : renderContent()}
                </Box>
            </Box>
        </Box>
    );
};

export default UserCourseScreen;